/*
 * Request Routing — four Hairline node figures (triage-intake / router / records / review).
 * Geometry and motion ported verbatim from RoutingSystem_Hairline.html.
 * Uses the shared Hairline kernel (MIT License, Copyright (c) 2026 Lucas Marques —
 * full notice in src/components/feature/hairline/kernel.ts).
 */
/* eslint-disable */
import {
  Cam, fit, proj, facing, rings, prism, solid, put, mk, poly, open, fillet,
  tween, tset, tval, tdone, register, pointer, disposer,
} from '@/components/feature/hairline/kernel';

export interface RoutingHandle {
  select: (on: boolean) => void;
  destroy: () => void;
}

type Ctx = { stage: any; svg: any; read: { textContent: string } };

function helpers(P, front) {
  function box(parent, x0, y0, x1, y1, z0, z1, r = 1.5) { const el = solid(parent), [a, b] = rings(x0, y0, x1, y1, r, 0.6); put(el, prism(P, front, a, b, z0, z1)); return el; }
  function path(parent, pts, cls = 'nf lo') { return mk('path', { class: cls, d: open(pts) }, parent); }
  function sheet(parent, x, y, z, w, h) {
    const q = (u, v) => P(x + u, y, z + v), points = [[0, 0], [w, 0], [w, h - 9], [w - 9, h], [0, h]];
    const face = mk('path', { class: 'sil', d: poly(fillet(points, points.map(() => 1)).map(([u, v]) => q(u, v))) }, parent);
    path(parent, [q(w - 9, h), q(w - 9, h - 9), q(w, h - 9)]);
    for (const [v, len] of [[h - 17, w * 0.58], [h - 25, w * 0.7], [h - 33, w * 0.45]]) path(parent, [q(8, v), q(8 + len, v)]);
    return face;
  }
  return { box, path, sheet };
}

function finish(stage, svg, read, bag, tw, drawRef, label) {
  let active = false;
  const B = register(stage, (_dt, now) => { const t = tval(tw, now); if (t !== drawRef.last) drawRef.draw(t); return !tdone(tw, now); });
  bag.add(B.unregister);
  function choose(on) { if (active === on) return; active = on; drawRef.active = on; tset(tw, on ? 1 : 0, performance.now(), 0); read.textContent = on ? label : 'rest'; B.wake(); }
  if (!stage.hasAttribute?.('data-controlled')) bag.add(pointer(stage, { move: ([x, y]) => choose(x > 45 && x < 355 && y > 40 && y < 285), leave: () => choose(false) }));
  bag.add(() => svg.replaceChildren());
  return { select: choose, destroy: bag.dispose };
}

export function mountIntake({ stage, svg, read }: Ctx, value: number): RoutingHandle {
  const bag = disposer(), C = Cam(45, 0.5, 2.05); const amount = value;
  fit(C, [[0, 0, 0], [76, 66, 0], [0, 0, 78], [76, 66, 78]], 200, 166);
  const P = proj(C), front = facing(C), g = mk('g', {}, svg), tw = tween(0);
  const { box, sheet } = helpers(P, front);
  box(g, 0, 0, 76, 64, 0, 3, 3); box(g, 0, 0, 76, 4, 3, 18); box(g, 0, 4, 3, 64, 3, 17);
  const papers = mk('g', {}, g);
  box(g, 73, 4, 76, 64, 3, 17);
  const lip = mk('path', { class: 'sil hi' }, g);
  const edge = [[0, 0], [76, 0], [76, 11], [47, 11], [43, 6], [33, 6], [29, 11], [0, 11]];
  lip.setAttribute('d', poly(fillet(edge, edge.map(() => 1)).map(([x, h]) => P(x, 64, 3 + h))));
  const ref: any = { last: NaN, active: false };
  ref.draw = (t) => {
    papers.replaceChildren();
    sheet(papers, 10, 20, 9 + t * amount * 8, 55, 56);
    const face = sheet(papers, 7, 32, 6 + t * amount * 5, 59, 49); face.classList.toggle('hi', ref.active);
    lip.classList.toggle('hi', !ref.active); ref.last = t;
  };
  ref.draw(0); read.textContent = 'rest';
  return finish(stage, svg, read, bag, tw, ref, 'received');
}

export function mountRouter({ stage, svg, read }: Ctx, value: number): RoutingHandle {
  const bag = disposer(), C = Cam(45, 0.5, 2.1); const amount = value;
  fit(C, [[0, 0, 0], [72, 72, 0], [0, 0, 56], [72, 72, 56]], 200, 166);
  const P = proj(C), front = facing(C), g = mk('g', {}, svg), tw = tween(0);
  const { box, path } = helpers(P, front);
  box(g, 0, 0, 72, 72, 0, 7, 5); box(g, 5, 5, 67, 67, 7, 27, 4);
  const lid = mk('g', {}, g);
  for (const x of [16, 27, 38, 49]) path(g, [P(x, 67, 13), P(x + 5, 67, 13)]);
  path(g, [P(67, 20, 14), P(67, 35, 14)]);
  const ref: any = { last: NaN, active: false };
  ref.draw = (t) => {
    lid.replaceChildren(); const z = 29 + t * amount * 9;
    const plate = box(lid, 9, 9, 63, 63, z, z + 4, 4); plate.sil.classList.toggle('hi', !ref.active);
    const chip = box(lid, 24, 24, 48, 48, z + 4, z + 9, 2); chip.sil.classList.toggle('hi', ref.active);
    for (const x of [29, 36, 43]) { path(lid, [P(x, 19, z + 4), P(x, 24, z + 4)]); path(lid, [P(x, 48, z + 4), P(x, 53, z + 4)]); }
    ref.last = t;
  };
  ref.draw(0); read.textContent = 'rest';
  return finish(stage, svg, read, bag, tw, ref, 'classify');
}

export function mountRecords({ stage, svg, read }: Ctx, value: number): RoutingHandle {
  const bag = disposer(), C = Cam(45, 0.5, 2.35); const amount = value;
  fit(C, [[0, 0, 0], [70, 40, 0], [0, 0, 80], [70, 40, 80]], 200, 166);
  const P = proj(C), front = facing(C), g = mk('g', {}, svg), tw = tween(0);
  const { box, path, sheet } = helpers(P, front);
  box(g, 0, 0, 70, 39, 0, 3, 3);
  const records = mk('g', {}, g);
  const ref: any = { last: NaN, active: false };
  ref.draw = (t) => {
    records.replaceChildren();
    sheet(records, 6, 5, 7, 56, 59);
    sheet(records, 4, 17, 5 + t * amount * 4, 58, 63);
    const face = sheet(records, 2, 29, 3 + t * amount * 7, 60, 66); face.classList.toggle('hi', ref.active);
    const q = (x, z) => P(x, 29, z + 3 + t * amount * 7);
    path(records, [q(10, 14), q(44, 14)]); path(records, [q(10, 7), q(34, 7)]);
    ref.last = t;
  };
  ref.draw(0); read.textContent = 'rest';
  return finish(stage, svg, read, bag, tw, ref, 'updated');
}

export function mountReview({ stage, svg, read }: Ctx, value: number): RoutingHandle {
  const bag = disposer(), C = Cam(45, 0.5, 2.3); const amount = value;
  fit(C, [[0, 0, 0], [74, 47, 0], [0, 0, 83], [74, 47, 83]], 200, 166);
  const P = proj(C), front = facing(C), g = mk('g', {}, svg), tw = tween(0);
  const { box, path, sheet } = helpers(P, front);
  box(g, 0, 0, 74, 46, 0, 3, 3); box(g, 0, 0, 74, 3, 3, 12); box(g, 0, 3, 3, 46, 3, 12);
  const clipboard = mk('g', {}, g);
  box(g, 71, 3, 74, 46, 3, 12);
  const ref: any = { last: NaN, active: false };
  ref.draw = (t) => {
    clipboard.replaceChildren(); const z = 5 + t * amount * 6;
    const outline = [[5, 0], [65, 0], [65, 72], [5, 72]];
    const board = mk('path', { class: 'sil', d: poly(fillet(outline, outline.map(() => 2)).map(([x, h]) => P(x, 26, z + h))) }, clipboard); board.classList.toggle('hi', ref.active);
    sheet(clipboard, 10, 28, z + 7, 49, 56);
    const clip = [[25, 63], [46, 63], [46, 73], [25, 73]];
    mk('path', { class: 'sil', d: poly(fillet(clip, clip.map(() => 2)).map(([x, h]) => P(x, 30, z + h))) }, clipboard);
    path(clipboard, [P(30, 30, z + 69), P(41, 30, z + 69)]);
    ref.last = t;
  };
  ref.draw(0); read.textContent = 'rest';
  return finish(stage, svg, read, bag, tw, ref, 'review');
}

export const routingMounts = {
  intake: mountIntake,
  router: mountRouter,
  records: mountRecords,
  review: mountReview,
};